import React, { useState, useEffect } from 'react';
import { createCheckoutSession } from '../api/stripe';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import logo from '../assets/logo.png';

interface SubscriptionGateProps {
  children: React.ReactNode;
}

const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Lock body scroll when subscription gate is active
    document.body.style.overflow = 'hidden';

    return () => {
      // Restore scroll on unmount
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleSubscribe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get the price ID from environment variable
      const priceId = import.meta.env.VITE_STRIPE_PRICE_ID;

      if (!priceId || priceId === 'price_1234567890') {
        throw new Error(
          'Stripe price ID not configured. Please contact support.'
        );
      }

      const session = await createCheckoutSession(priceId);

      // Redirect to Stripe Checkout
      if (session.url) {
        window.location.href = session.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(
        err.message || 'Failed to start checkout process. Please try again.'
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 h-screen w-screen overflow-hidden">
      {/* Blurred content */}
      <div className="h-full w-full filter blur-md pointer-events-none select-none overflow-hidden">
        {children}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-filter backdrop-blur-sm flex items-center justify-center">
        <div className="bg-grey rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl border border-white border-opacity-20">
          <div className="text-center">
            <img src={logo} alt="Logo" className="h-12 mx-auto mb-6" />

            <h2 className="text-2xl font-bold text-white mb-4">
              Unlock Full Access
            </h2>

            <p className="text-white text-opacity-80 mb-8">
              Subscribe to continue using all features and managing your social
              media comments like a pro.
            </p>

            {error && (
              <div className="bg-red bg-opacity-20 border border-red text-red rounded-lg p-3 mb-6 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleSubscribe}
              disabled={isLoading}
              className={`
                w-full bg-violet hover:bg-violet-700 text-white font-bold py-3 px-6 rounded-full 
                transition-all duration-200 flex items-center justify-center gap-2 mb-4
                ${
                  isLoading
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:shadow-lg'
                }
              `}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Setting up...</span>
                </>
              ) : (
                <>
                  <CreditCardIcon className="w-5 h-5" />
                  <span>Subscribe Now</span>
                </>
              )}
            </button>

            <p className="text-xs text-white text-opacity-60">
              Cancel anytime. Secure payment via Stripe.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGate;
